const green = "bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-300";
const amber = "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300";
const orange = "bg-orange-100 text-orange-700 dark:bg-orange-500/20 dark:text-orange-300";
const red = "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-300";
const gold = "bg-riot-gold-100 text-riot-gold-600 dark:bg-riot-gold-500/20 dark:text-riot-gold-400";
const gray = "bg-gray-100 text-gray-600 dark:bg-riot-navy-700 dark:text-riot-navy-300";

const badgeStyles: Record<string, string> = {
  // Device statuses
  online: green,
  // Stale: device hasn't checked in within its expected cadence but
  // hasn't crossed the offline threshold. Server-derived from
  // lastSeen + checkInMinutes; never stored.
  stale: amber,
  offline: gray,
  inactive: gray,
  updating: gold,
  error: red,

  // Firmware statuses
  active: green,
  draft: amber,
  deprecated: orange,

  // Task statuses
  scheduled: amber,
  queued: amber,
  in_progress: gold,
  paused: orange,
  completed: green,
  completed_with_errors: amber,
  failed: red,
  expired: amber,
  cancelled: red,

  // Device task statuses
  pending: amber,
  downloading: gold,
  installing: gold,
  succeeded: green,
  timed_out: red,

  // Company statuses
  suspended: orange,
};

// Dot colors for the `variant="dot"` rendering. Mirrors badgeStyles
// but maps to a single Tailwind `bg-*` class for the dot only. Kept
// in sync with styles/tokens.ts colors.status — these dot colors are
// the same as what the dashboard's donut/legend swatches use, so the
// "status indicator" reads consistently across summary cards and
// in-row badges.
const greenDot = "bg-green-500";
const amberDot = "bg-amber-500";
const orangeDot = "bg-orange-500";
const redDot = "bg-red-500";
const goldDot = "bg-riot-gold-500";
const grayDot = "bg-gray-400 dark:bg-riot-navy-400";
const lightGrayDot = "bg-gray-300 dark:bg-riot-navy-500";

const dotColors: Record<string, string> = {
  online: greenDot,
  stale: amberDot,
  offline: grayDot,
  inactive: lightGrayDot,
  updating: "bg-blue-500",
  error: redDot,

  active: greenDot,
  draft: amberDot,
  deprecated: orangeDot,

  scheduled: amberDot,
  queued: amberDot,
  in_progress: goldDot,
  paused: orangeDot,
  completed: greenDot,
  completed_with_errors: amberDot,
  failed: redDot,
  expired: amberDot,
  cancelled: redDot,

  pending: amberDot,
  downloading: goldDot,
  installing: goldDot,
  succeeded: greenDot,
  timed_out: redDot,

  suspended: orangeDot,
};

interface StatusBadgeProps {
  status: string;
  count?: number;
  /**
   * Visual style.
   * - `"pill"` (default) — the existing rounded-pill render. Used
   *   for firmware / task / company statuses where the pill ties
   *   the colored background to the row.
   * - `"dot"` — colored dot + plain label, matching the dashboard's
   *   summary-card legend. Used for device statuses so a row's
   *   Status cell reads the same way as the legend (same dot color,
   *   same casing, no second visual vocabulary).
   */
  variant?: "pill" | "dot";
}

export function StatusBadge({ status, count, variant = "pill" }: StatusBadgeProps) {
  const label = status.replace(/_/g, " ");

  if (variant === "dot") {
    const dot = dotColors[status] ?? grayDot;
    return (
      <span className="inline-flex items-center gap-2 text-xs font-medium capitalize text-gray-700 dark:text-riot-navy-200">
        <span className={`h-2.5 w-2.5 rounded-full ${dot}`} />
        {count != null ? `${count} ${label}` : label}
      </span>
    );
  }

  const style = badgeStyles[status] ?? gray;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-medium capitalize ${style}`}>
      {count != null ? `${count} ${label}` : label}
    </span>
  );
}
